// The signed-in clinician's agents: list, unseen count, live updates.
// Lives in MedStudentClinicPage (always mounted) so the badges work on every tab and while the
// right panel is collapsed. Realtime does the work; a slow poll (every 30 s, only while the tab is
// visible) covers a dropped socket, and the list is read again when the tab comes back and each
// time the channel joins, to catch up on whatever a dropped socket missed. The poll is every 5 s
// instead while any agent has a run going, and for 2 minutes from when runs waiting in the queue
// are first seen (runs aren't on realtime, and "Now & next" (NowNext) shows them; under the daily
// limit a run can stay queued for hours, so a queue alone doesn't keep the fast pace). Never after a
// read that failed: a server that is down is asked every 30 s. An open agent chat polls its own
// agent much the same way.
//
// Realtime is only a "something changed, look again" signal. The rows it carries can hold
// patient details, so the handler never reads them and nothing here logs them: the list always
// comes from the API. Only agent_messages and agent_cards are watched. agent_runs is not
// published at all: a run's row holds more than the API ever shows, and an open chat polls for
// its runs.
//
// Two different `enabled`: the one passed in says whether this user may use agents at all (the
// caller's role gate); the one returned is the server's own switch, from GET /api/agents/.
//
// View-as: list calls go out with the viewed user's token, but the realtime socket belongs to
// supabasePublic() and carries the admin's own session. Its events then concern the admin's own
// agents; the viewed user's list stays current through the poll.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabasePublic } from '../../../components/supabaseClient';
import { uniqueChannelTopic } from '../../../utils/realtimeChannel';
import { listAgents } from './agentsService';
// The rule "Now & next" shows by: an agent's run going ("Working now"), runs queued and none going.
import { isQueued, runningRun } from './nowNextRows';

const POLL_MS = 30000;
const ACTIVE_POLL_MS = 5000;
// How long runs waiting in the queue keep the fast poll, from when that queue was first seen.
const QUEUED_FAST_MS = 2 * 60 * 1000;
// A burst of events is one reload: it fires once the events have been quiet for SETTLE_MS, or
// MAX_WAIT_MS after the burst began, whichever comes first, so a steady stream still reloads.
const SETTLE_MS = 400;
const MAX_WAIT_MS = 2000;
const TABLES = ['agent_messages', 'agent_cards'];
// `worker`: whether the server executes runs (its agents worker is up); an older server that
// doesn't say counts as yes. `runtime`: whether its runs have an agent service to run on: 'ok',
// 'missing' (none set up) or 'unreachable' (not answering just now); anything else counts as 'ok'.
const EMPTY = { agents: [], types: [], enabled: false, worker: true, runtime: 'ok', loaded: false, error: null };
const RUNTIME_PROBLEMS = new Set(['missing', 'unreachable']);

const unseenOf = (agent) => (Number.isFinite(agent?.unseen) && agent.unseen > 0 ? agent.unseen : 0);
const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);
// Who is queued: the ids of the agents with runs queued and none going, as one text, so a queue is
// "the same" while the same agents wait in it, whatever their counts.
const queuedKeyOf = (agents) => agents.filter(isQueued).map((agent) => String(agent.id)).sort().join(',');

export function useAgents({ enabled = true } = {}) {
  const [state, setState] = useState(EMPTY);
  const [version, setVersion] = useState(0);
  const settleTimer = useRef(null);
  const maxWaitTimer = useRef(null);
  // Loads are numbered. An answer is shown unless a newer load's answer is already shown (or the
  // hook was switched off since), so a slow old answer can't overwrite a fresh one, while
  // overlapping loads still show whatever arrives in order.
  const started = useRef(0);
  const shown = useRef(0);
  const reading = useRef(0); // reads still waiting for their answer
  const lastRead = useRef(0); // when the newest read went out

  const refresh = useCallback(async () => {
    if (!enabled) return;
    started.current += 1;
    const mine = started.current;
    reading.current += 1;
    lastRead.current = Date.now();
    try {
      const data = await listAgents();
      if (mine <= shown.current) return;
      shown.current = mine;
      const next = {
        agents: Array.isArray(data?.agents) ? data.agents : [],
        types: Array.isArray(data?.types) ? data.types : [],
        enabled: !!data?.enabled,
        worker: data?.worker !== false,
        runtime: RUNTIME_PROBLEMS.has(data?.runtime) ? data.runtime : 'ok',
        loaded: true,
        error: null,
      };
      // A poll that finds the same list keeps the old state, so the page this hook lives in
      // (always mounted, and large) doesn't re-render at every poll for nothing.
      setState((prev) => (
        prev.loaded && prev.error === null && prev.enabled === next.enabled && prev.worker === next.worker
        && prev.runtime === next.runtime
        && sameJson(prev.agents, next.agents) && sameJson(prev.types, next.types) ? prev : next
      ));
    } catch (err) {
      if (mine <= shown.current) return;
      shown.current = mine;
      setState((prev) => ({ ...prev, loaded: true, error: err?.message || "Couldn't load agents." }));
    } finally {
      reading.current -= 1;
    }
  }, [enabled]);

  // The end of a burst of events: one version bump, which an open chat reloads on, and one load.
  const flush = useCallback(() => {
    clearTimeout(settleTimer.current);
    clearTimeout(maxWaitTimer.current);
    settleTimer.current = null;
    maxWaitTimer.current = null;
    setVersion((v) => v + 1);
    refresh();
  }, [refresh]);

  const onChange = useCallback(() => {
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(flush, SETTLE_MS);
    if (maxWaitTimer.current === null) maxWaitTimer.current = setTimeout(flush, MAX_WAIT_MS);
  }, [flush]);

  // Load now. Back on the tab after a spell in the background, load again at once rather than at
  // the next tick. Switched off (or unmounted), it forgets the list, so a badge never outlives the
  // access that earned it.
  useEffect(() => {
    if (!enabled) {
      setState(EMPTY);
      return undefined;
    }
    refresh();
    const whileVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', whileVisible);
    return () => {
      document.removeEventListener('visibilitychange', whileVisible);
      shown.current = started.current;
    };
  }, [enabled, refresh]);

  // Runs waiting in the queue keep the fast pace for QUEUED_FAST_MS from when this hook first saw
  // who is queued; a change in who is queued starts it again, and so does a queue that empties and
  // fills again. A read that fails keeps the list, so it changes nothing here.
  const queuedKey = useMemo(() => queuedKeyOf(state.agents), [state.agents]);
  const [queueFresh, setQueueFresh] = useState(false);
  useEffect(() => {
    if (!queuedKey) {
      setQueueFresh(false);
      return undefined;
    }
    setQueueFresh(true);
    const spent = setTimeout(() => setQueueFresh(false), QUEUED_FAST_MS);
    return () => clearTimeout(spent);
  }, [queuedKey]);
  const running = useMemo(() => state.agents.some((agent) => runningRun(agent) !== null), [state.agents]);

  // The poll: every 5 s while a run is going or a fresh queue waits, but never after a read that
  // failed (the list kept from before it may say a run is going, and a server that is down is asked
  // every 30 s, not every 5 s); otherwise every 30 s. Only while the tab is visible. While a read is
  // still out, a fast tick waits for it, so a slow server never gets reads stacked up, but no longer
  // than the slow poll's period from when the newest read went out (authedFetch has no timeout), so a
  // read that never answers never stops the polling (the gap is taken either way, so a clock set back
  // while a read hangs can't hold it either). Its own effect, so a change of pace starts no load and
  // drops no answer on its way.
  const busy = !state.error && (running || queueFresh);
  useEffect(() => {
    if (!enabled) return undefined;
    const poll = setInterval(() => {
      if (document.hidden) return;
      if (busy && reading.current > 0 && Math.abs(Date.now() - lastRead.current) < POLL_MS) return;
      refresh();
    }, busy ? ACTIVE_POLL_MS : POLL_MS);
    return () => clearInterval(poll);
  }, [enabled, refresh, busy]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let client = null;
    let channel = null;
    let stopWaiting = null;
    // removeChannel is async, so an event can still arrive after this cleanup has run.
    const signal = () => { if (!cancelled) onChange(); };
    const onStatus = (status) => {
      if (cancelled) return;
      // Joined, and joined again after every dropped socket: read the list for whatever was
      // missed meanwhile. (The first join covers what happened between the first load and it.)
      if (status === 'SUBSCRIBED') {
        refresh();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        // The status word only, never the error that comes with it.
        console.warn('[agents] realtime channel:', status);
      }
    };
    const connect = async (mayRetry) => {
      try {
        client = supabasePublic();
        if (client.realtime?.setAuth) {
          // supabase-js gives the realtime socket the signed-in user's token asynchronously. A
          // channel that joins before it lands joins as anon, the member-only policies show it
          // nothing, and no event ever comes. So wait for the token first.
          try {
            await client.realtime.setAuth();
          } catch (err) {
            if (cancelled) return;
            console.warn('[agents] realtime unavailable:', err?.message || err);
            if (mayRetry) retryWhenVisible();
            return;
          }
        }
        if (cancelled) return;
        channel = client.channel(uniqueChannelTopic('agents'));
        TABLES.forEach((table) => {
          channel = channel.on('postgres_changes', { event: '*', schema: 'public', table }, signal);
        });
        channel.subscribe(onStatus);
      } catch (err) {
        console.warn('[agents] realtime unavailable:', err?.message || err);
      }
    };
    // A failed token lookup is tried once more, the next time the tab is looked at.
    const retryWhenVisible = () => {
      const onVisible = () => {
        if (document.hidden) return;
        stopWaiting();
        connect(false);
      };
      document.addEventListener('visibilitychange', onVisible);
      stopWaiting = () => {
        document.removeEventListener('visibilitychange', onVisible);
        stopWaiting = null;
      };
    };
    connect(true);
    return () => {
      cancelled = true;
      if (stopWaiting) stopWaiting();
      clearTimeout(settleTimer.current);
      clearTimeout(maxWaitTimer.current);
      settleTimer.current = null;
      maxWaitTimer.current = null;
      if (!channel) return;
      const warn = (err) => console.warn('[agents] channel cleanup failed:', err?.message || err);
      try { Promise.resolve(client.removeChannel(channel)).catch(warn); } catch (err) { warn(err); }
    };
  }, [enabled, onChange, refresh]);

  const unseen = useMemo(() => state.agents.reduce((n, a) => n + unseenOf(a), 0), [state.agents]);
  const { loaded, ...rest } = state;
  return { ...rest, loading: enabled && !loaded, unseen, version, refresh };
}
